// Client-safe view-model for the product pricing section. The server assembler
// (`product-prices-section.tsx`) builds it once and hands the same payload to
// whichever variant the A/B arm selects — the seller-card control or the tier
// ladder treatment — so both read identical numbers.
import type { TooltipData } from '@/components/client/product/feature-tooltip'
import type { CouponCalloutCoupon, CouponCalloutText } from '@/components/client/shared/coupon-callout'

import type { UpgradeComparison } from './upgrade-comparison'

export interface PriceLevelTab {
  id: number
  /** Translated ordinal label ("Premium", "Advanced"…). */
  name: string
  /** Ascending display order: Premium is 1, then Advanced, Standard, and so on. */
  sortOrder: number
  /** Raw levels.name token ("9", "7", "L90"…) — the tier code and the ?level= deep-link value. */
  levelName: string
  /** Lowest offer, reduced by a coupon only when `discount > 0`; null when no seller lists a price. */
  lowestPrice: number | null
  /** priceType of the offer that set `lowestPrice`. */
  lowestPriceType: string | null
  sellerCount: number
}

export interface PriceSellerData {
  sellerId: number
  sellerName: string
  /** Editorial seller priority for the primary deal; comparisons still sort by price. */
  sellerPosition?: number
  sellerTagline: string
  sellerBlurb: string
  sellerRating: number | null
  sellerReviews: number | null
  sellerFeatures: string[]
  sellerType: string
  logoUrl: string | null
  /** Sticker price; 0 when the seller lists no price. */
  price: number
  discountPrice: number | null
  discount: number | null
  url: string | null
  coupon: CouponCalloutCoupon | null
  couponLabel: string | null
  offerTagline: string | null
  priceType: string | null
  sponsored?: boolean
  care?: {
    type: string
    summary: string
    fitting: string | null
    followUp: string | null
    trial: string | null
    warranty: string | null
    charges: string | null
  }
}

export interface PriceLevelData {
  levelId: number
  sellers: PriceSellerData[]
  upgradeComparisons: UpgradeComparison[]
}

export interface PriceModelData {
  id: number
  name: string
  fullName: string
  imageUrl: string | null
  levels: PriceLevelTab[]
  levelData: Record<number, PriceLevelData>
  defaultLevelId: number
}

export interface PricesSectionProps {
  releaseFullName: string
  releaseSlug: string
  models: PriceModelData[]
  defaultModelId: number
  /** Upgrade-highlight tooltips, keyed by the `tooltipId` stamped on each row. */
  tooltips: Record<string, TooltipData>
  couponText: CouponCalloutText
}

/** The row's effective price under the one discount rule every surface shares. */
export const effectiveSellerPrice = (seller: Pick<PriceSellerData, 'price' | 'discount' | 'discountPrice'>) =>
  seller.discount != null && seller.discount > 0 && seller.discountPrice != null ? seller.discountPrice : seller.price
